<template>
	<section class="view-pad-tutorial">
		<h6 class="color-chalky-grey-2" v-if="!showIntroductionModal">Tutorial</h6>

		<transition-group name="tutorial-step" tag="div" class="steps" v-if="!showIntroductionModal">
			<section class="tutorial-steps">
				<div class="step" v-for="(step, index) in visibleSteps" v-bind:key="index">
					<p class="type-heading-3 push-top-sm">{{ step.caption }}</p>
				</div>
			</section>
		</transition-group>

		<div class="dots" v-if="!showIntroductionModal">
			<div
				class="step"
				v-for="(step, index) in steps"
				v-bind:class="{
					active: index === currentStepIndex,
					done: index < currentStepIndex,
				}"
				v-bind:key="index"
			>
				<div class="circle"></div>
			</div>
		</div>

		<!-- Introduction Modal -->
		<div class="tutorial-introduction" v-if="showIntroductionModal">
			<div class="content">
				<img
					class="tint-chalky-white"
					height="50"
					src="~@chalkysticks/sass/build/asset/image/icon/rack-9ball.svg"
				/>

				<h3 class="push-top push-bottom">Welcome to the Pad tutorial!</h3>
				<p>We're about to begin. Please follow the steps to understand how to use our diagramming tool.</p>
				<button class="size-small" v-on:click="Handle_OnClickStart">Start</button>
			</div>
		</div>

		<!-- Completion Modal -->
		<div class="tutorial-complete" v-if="showCompleteModal">
			<div class="content">
				<img
					class="tint-chalky-white"
					height="50"
					src="~@chalkysticks/sass/build/asset/image/icon/checkmark.svg"
				/>

				<h3 class="push-top push-bottom">You've learned the basics!</h3>
				<p>Great job! You're ready to start creating diagrams.</p>
				<button class="size-small" v-on:click="Handle_OnClickContinue">Continue</button>
			</div>
		</div>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component } from 'vue-property-decorator';
	import { beforeDestroy, mounted, bind } from '@/Utility/Decorators';

	/** A single tutorial step definition */
	export interface IStep {
		caption: string;
		event: string;
	}

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Pad
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewPadTutorial extends ViewBase {
		/**
		 * Only render the active step to enable smooth transitions
		 *
		 * @return IStep[]
		 */
		protected get visibleSteps(): IStep[] {
			if (this.currentStepIndex >= this.steps.length) {
				return [];
			}

			return [this.steps[this.currentStepIndex]];
		}

		/**
		 * Steps for the tutorial
		 *
		 * @type IStep[]
		 */
		protected steps: IStep[] = [
			{
				caption: 'Double tap the table to add a ball',
				event: 'pad.ball:add',
			},
			{
				caption: 'Double tap somewhere else to make a line',
				event: 'pad.line:add',
			},
			{
				caption: 'Drag to move the ghost ball',
				event: 'pad.ball:move',
			},
			{
				caption: 'Tap the table to deselect',
				event: 'pad.ball:deselect',
			},
			{
				caption: 'Tap near a ball to select it',
				event: 'pad.ball:select',
			},
			{
				caption: 'Select a different ball from the tray',
				event: 'balltray:select',
			},
			{
				caption: 'Press and hold the table to remove it',
				event: 'pad.ball:remove',
			},
		];

		/**
		 * Index for currently active step
		 *
		 * @type number
		 */
		protected currentStepIndex: number = 0;

		/**
		 * Whether the introduction modal should be visible
		 *
		 * @type boolean
		 */
		protected showIntroductionModal: boolean = true;

		/**
		 * Whether the completion modal should be visible
		 *
		 * @type boolean
		 */
		protected showCompleteModal: boolean = false;

		/**
		 * The step event name we're currently listening to
		 *
		 * @type string|null
		 */
		protected currentEventName: string | null = null;

		/**
		 * Initialize tutorial state
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			this.showIntroductionModal = true;
			this.currentStepIndex = 0;
		}

		/**
		 * Attach global event listeners
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			if (!this.showIntroductionModal) {
				this.attachStepEvent();
			}
		}

		/**
		 * Detach global event listeners
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			this.detachStepEvent();
		}

		/**
		 * Subscribe to the current step's event
		 *
		 * @return void
		 */
		protected attachStepEvent(): void {
			this.detachStepEvent();

			if (this.currentStepIndex >= this.steps.length) {
				setTimeout(() => (this.showCompleteModal = true), 500);
				return;
			}

			this.currentEventName = this.steps[this.currentStepIndex].event;
			ChalkySticks.Core.Event.Bus.on(this.currentEventName, this.Handle_OnStepEvent);
		}

		/**
		 * Unsubscribe from the current step's event
		 *
		 * @return void
		 */
		protected detachStepEvent(): void {
			if (this.currentEventName) {
				ChalkySticks.Core.Event.Bus.off(this.currentEventName, this.Handle_OnStepEvent);
				this.currentEventName = null;
			}
		}

		/**
		 * Advance to the next tutorial step
		 *
		 * @return void
		 */
		protected Action_AdvanceStep(): void {
			this.detachStepEvent();
			this.currentStepIndex += 1;
			this.attachStepEvent();
		}

		/**
		 * Advance to the next step when its event fires
		 *
		 * @param ChalkySticks.Core.IDispatcherEvent<any> e
		 * @return void
		 */
		@bind
		protected Handle_OnStepEvent(e: ChalkySticks.Core.IDispatcherEvent<any>): void {
			this.Action_AdvanceStep();
		}

		/**
		 * Handle the Continue button on completion modal
		 *
		 * @return void
		 */
		@bind
		protected Handle_OnClickContinue(): void {
			this.showCompleteModal = false;

			this.$emit('complete');
			ChalkySticks.Core.Event.Bus.dispatch('pad.tutorial:complete');
		}

		/**
		 * Handle the Start button on introduction modal
		 *
		 * @return void
		 */
		@bind
		protected Handle_OnClickStart(): void {
			this.showIntroductionModal = false;
			this.attachStepEvent();

			this.$emit('start');
			ChalkySticks.Core.Event.Bus.dispatch('pad.tutorial:start');
		}
	}
</script>

<style lang="scss">
	.view-pad-tutorial {
		background: linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 20%, rgba(0, 0, 0, 0) 50%);
		height: 100%;
		left: 0;
		padding: 1rem;
		pointer-events: none;
		position: absolute;
		text-align: center;
		top: 0;
		width: 100%;

		button {
			pointer-events: auto;
		}

		.steps {
			overflow: hidden;
			position: relative;
		}

		.dots {
			display: flex;
			gap: 8px;
			justify-content: center;
			margin-top: 1rem;

			.step {
				opacity: 0.3;

				&.active {
					opacity: 1;
				}

				.circle {
					background: var(--chalky-white);
					border-radius: 50%;
					height: 4px;
					width: 4px;

					&.done {
						background: var(--chalky-grey-1);
					}
				}
			}
		}

		.tutorial-introduction,
		.tutorial-complete {
			align-items: center;
			background: rgba(0, 0, 0, 0.85);
			bottom: 0;
			display: flex;
			justify-content: center;
			left: 0;
			position: fixed;
			right: 0;
			top: 0;
			z-index: 9999;

			.content {
				max-width: 480px;
				padding: 2rem;
				text-align: center;
			}
		}
	}
</style>
